//
// Functions for deriving information from the reducer state.
//
import {type Moment} from 'moment-timezone';

import {
    type BaseState,
    type CreatePlanState,
    type EditPlanState,
    type CompoundView,
} from 'web-app/react/simplified-us-billing-plan/types';
import {hasValue} from '@famly/stat_ts-utils_has-value';

/**
 * Returns the view and potential sub-view of the state.
 *
 * Returns undefined if the state is a naked `BaseState` or some unknown subtype of `BaseState`.
 */
export const getCompoundView = (state: BaseState): CompoundView | undefined => {
    if (isCreate(state)) {
        return {
            view: state.view,
            subView: state.subView,
        };
    }
    if (isEdit(state)) {
        return {
            view: state.view,
        };
    }
    return undefined;
};

/**
 * True iff there is a future plan relative to the created plan.
 *
 * If at least one child of the selected children has a plan that starts strictly after the configured start date of the plan being created, it is considered future.
 */
export const hasFuturePlansForCreate = (state: CreatePlanState): boolean => {
    const startDate = startDateForCreate(state);
    if (!hasValue(startDate)) {
        return false;
    }
    return state.childrenState.selectedChildren.some(child =>
        child.plans.some(plan => plan.startDate.isAfter(startDate)),
    );
};

/**
 * The start date of the created plan.
 *
 * Returns `null` if the start date is not available.
 */
export const startDateForCreate = (state: CreatePlanState): Moment | null => {
    if (!hasValue(state.billingPeriodState.selectedBillingPeriod)) {
        return null;
    }
    return state.billingPeriodState.selectedBillingPeriod.unformattedStartDate ?? null;
};

/**
 * True iff the state is in create view.
 */
export const isCreate = (state: BaseState): state is CreatePlanState => {
    const stateWithView = state as unknown as {view: string};
    return hasValue(stateWithView) && stateWithView.view === 'create';
};

/**
 * True iff the state is in the single child create plan view.
 */
export const isCreateSingle = (state: BaseState): state is CreatePlanState => {
    if (!isCreate(state)) {
        return false;
    }
    return state.subView === 'single';
};

/**
 * True iff the state is in edit view.
 */
export const isEdit = (state: BaseState): state is EditPlanState => {
    const stateWithView = state as unknown as {view: string};
    return hasValue(stateWithView) && stateWithView.view === 'edit';
};
